import type { AxiosInstance } from 'axios';

export interface TimeResponse {
  currentTime: string;
  isManipulated: boolean;
  systemTime: string;
  success?: boolean;
  message?: string;
}

export interface AdvanceTimeRequest {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

export interface AdvanceTimeResponse extends TimeResponse {
  previousTime: string;
}

/**
 * Creates a time manipulation API client with the provided axios instance.
 * This factory pattern allows both admin portal and mobile app to use their
 * own configured axios clients.
 *
 * @param axiosInstance - The axios instance to use for API calls
 * @returns Time manipulation API client
 */
export const createTimeApi = (axiosInstance: AxiosInstance) => ({
  /**
   * Get current server time
   */
  getCurrentTime: async (): Promise<TimeResponse> => {
    const response = await axiosInstance.get<TimeResponse>('/api/dev/time');
    return response.data;
  },

  /**
   * Set time to specific point
   */
  setTime: async (time: Date): Promise<TimeResponse> => {
    const response = await axiosInstance.post<TimeResponse>('/api/dev/time', {
      time: time.toISOString(),
    });
    return response.data;
  },

  /**
   * Advance time by specified units
   */
  advanceTime: async (
    request: AdvanceTimeRequest
  ): Promise<AdvanceTimeResponse> => {
    const response = await axiosInstance.patch<AdvanceTimeResponse>(
      '/api/dev/time',
      request
    );
    return response.data;
  },

  /**
   * Reset to system time
   */
  resetTime: async (): Promise<TimeResponse> => {
    const response = await axiosInstance.delete<TimeResponse>('/api/dev/time');
    return response.data;
  },
});

/**
 * Type representing the time manipulation API client
 */
export type TimeApi = ReturnType<typeof createTimeApi>;
